/* ═══════════ HAR SYNC TAB (Admin ▸ Data) ═══════════
   RWB-only bridge: add-only import from the SpecForma_HAR_Sync workbook the
   Power Automate flow maintains. Visible only to HAR_SYNC_EMAIL on desktop
   (gated in AdminPage). TWO-STEP: Preview computes a read-only plan; rows can
   be deselected (click toggles, ctrl+click toggles, shift+click ranges) and
   ONLY the selected rows are written on Apply. Startup sync just notifies
   unless the auto-apply pref is on. */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getHarSyncSource,
  setHarSyncSource,
  getHarAutoApply,
  setHarAutoApply,
  computeHarSyncPlan,
  applyHarSyncPlan,
  type HarSyncPlan,
  type HarSyncSummary,
} from '../services/harSyncService';

const inputStyle: React.CSSProperties = {
  padding: '7px 10px',
  borderRadius: 5,
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 13,
  width: '100%',
};
const btnPrimary: React.CSSProperties = {
  padding: '7px 16px',
  borderRadius: 6,
  border: 'none',
  background: 'var(--ac)',
  color: 'var(--color-text-inverse)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const btnSecondary: React.CSSProperties = {
  padding: '7px 16px',
  borderRadius: 6,
  border: '1px solid var(--bd)',
  background: 'var(--sf)',
  color: 'var(--tx)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const linkBtn: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--ac)',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  padding: '2px 6px',
};

type Group = 'Projects' | 'Deliverables' | 'Assignments' | 'Forecasts';
interface PlanRow {
  id: string;
  group: Group;
  label: string;
  sub: string;
}

/** Flatten the plan into selectable rows. Forecasts select at the
 *  member+project (weekly) level — the day-cells follow the pair. */
function planToRows(plan: HarSyncPlan): PlanRow[] {
  const rows: PlanRow[] = [];
  for (const p of plan.newProjects) {
    rows.push({
      id: `P|${p.projectNo}`,
      group: 'Projects',
      label: `${p.projectNo} — ${p.name}`,
      sub: p.client || '',
    });
  }
  for (const d of plan.newDeliverables) {
    rows.push({
      id: `D|${d.projectNo}|${d.name.toLowerCase()}`,
      group: 'Deliverables',
      label: `${d.projectNo} · ${d.name}`,
      sub: d.dueDate ? `due ${d.dueDate}` : 'no date',
    });
  }
  for (const a of plan.newAssignments) {
    rows.push({
      id: `A|${a.projectNo}|${a.memberId}`,
      group: 'Assignments',
      label: `${a.memberName} → ${a.projectNo}`,
      sub: a.role,
    });
  }
  for (const f of plan.forecastPairsList) {
    rows.push({
      id: `F|${f.projectNo}|${f.memberId}`,
      group: 'Forecasts',
      label: `${f.memberName} → ${f.projectNo}`,
      sub: `${f.hours}h · week of ${plan.weekStart}`,
    });
  }
  return rows;
}

/** Filter the plan down to the selected row ids before Apply. */
function filterPlan(plan: HarSyncPlan, selected: Set<string>): HarSyncPlan {
  const keptPairs = plan.forecastPairsList.filter((f) =>
    selected.has(`F|${f.projectNo}|${f.memberId}`)
  );
  const keptPairSet = new Set(keptPairs.map((f) => `${f.projectNo}|${f.memberId}`));
  return {
    ...plan,
    newProjects: plan.newProjects.filter((p) => selected.has(`P|${p.projectNo}`)),
    newDeliverables: plan.newDeliverables.filter((d) =>
      selected.has(`D|${d.projectNo}|${d.name.toLowerCase()}`)
    ),
    newAssignments: plan.newAssignments.filter((a) =>
      selected.has(`A|${a.projectNo}|${a.memberId}`)
    ),
    forecastPairsList: keptPairs,
    forecastCells: plan.forecastCells.filter((c) =>
      keptPairSet.has(`${c.projectNo}|${c.memberId}`)
    ),
  };
}

interface HarSyncTabProps {
  toast: (msg: string, isError?: boolean) => void;
}

export function HarSyncTab({ toast }: HarSyncTabProps) {
  const [source, setSource] = useState('');
  const [autoApply, setAuto] = useState(false);
  // Prefs live in a file (via IPC) — hydrate once on mount.
  useEffect(() => {
    void (async () => {
      setSource(await getHarSyncSource());
      setAuto(await getHarAutoApply());
    })();
  }, []);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [plan, setPlan] = useState<HarSyncPlan | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<HarSyncSummary | null>(null);
  const [error, setError] = useState('');
  const anchorRef = useRef<number | null>(null);

  const rows = useMemo(() => (plan ? planToRows(plan) : []), [plan]);

  const browse = async () => {
    const p = await window.api.openHarWorkbookDialog();
    if (p) {
      setSource(p);
      await setHarSyncSource(p);
    }
  };

  const preview = async () => {
    const s = source.trim();
    await setHarSyncSource(s);
    if (!s) {
      toast('Set the workbook path or share link first', true);
      return;
    }
    setBusy(true);
    setError('');
    setResult(null);
    setPlan(null);
    try {
      const p = await computeHarSyncPlan(setProgress);
      if (p) {
        setPlan(p);
        setSelected(new Set(planToRows(p).map((r) => r.id))); // everything in by default
        anchorRef.current = null;
        if (p.total === 0) toast('Already up to date — nothing to add');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      toast('HAR preview failed', true);
    } finally {
      setBusy(false);
      setProgress('');
    }
  };

  /** Explorer-style: click / ctrl+click toggles the row; shift+click applies
   *  the clicked row's NEW state to the whole range from the last click. */
  const onRowClick = (e: React.MouseEvent, idx: number) => {
    const next = new Set(selected);
    const id = rows[idx].id;
    const newState = !next.has(id);
    if (e.shiftKey && anchorRef.current !== null) {
      const [a, b] = [Math.min(anchorRef.current, idx), Math.max(anchorRef.current, idx)];
      for (let i = a; i <= b; i++) {
        if (newState) next.add(rows[i].id);
        else next.delete(rows[i].id);
      }
    } else {
      if (newState) next.add(id);
      else next.delete(id);
      anchorRef.current = idx;
    }
    setSelected(next);
  };

  const setGroup = (group: Group | null, on: boolean) => {
    const next = new Set(selected);
    for (const r of rows) {
      if (group && r.group !== group) continue;
      if (on) next.add(r.id);
      else next.delete(r.id);
    }
    setSelected(next);
  };

  const apply = async () => {
    if (!plan) return;
    setBusy(true);
    setError('');
    try {
      const filtered = filterPlan(plan, selected);
      const res = await applyHarSyncPlan(filtered, setProgress);
      setResult(res);
      setPlan(null);
      toast(
        `HAR sync applied: +${res.projectsAdded} projects, +${res.deliverablesAdded} deliverables, ` +
          `+${res.assignmentsAdded} assignments, ${res.forecastCellsSet} forecast cells`
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      toast('HAR apply failed', true);
    } finally {
      setBusy(false);
      setProgress('');
    }
  };

  const selCount = rows.filter((r) => selected.has(r.id)).length;
  const groups: Group[] = ['Projects', 'Deliverables', 'Assignments', 'Forecasts'];

  return (
    <div style={{ maxWidth: 760 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 4px' }}>HAR Sync</h3>
      <p style={{ fontSize: 13, color: 'var(--tm)', margin: '0 0 16px', lineHeight: 1.5 }}>
        Add-only import from the SpecForma_HAR_Sync workbook (RWB bridge). Preview shows every
        change first — nothing is written until you Apply, and you can deselect any row (click to
        toggle, Shift+click for a range). It never updates or deletes existing records.
      </p>

      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
        Workbook source
      </label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          style={inputStyle}
          value={source}
          placeholder="Local path to SpecForma_HAR_Sync.xlsx or a OneDrive share link"
          onChange={(e) => setSource(e.target.value)}
          onBlur={() => void setHarSyncSource(source.trim())}
          disabled={busy}
        />
        <button style={btnSecondary} onClick={browse} disabled={busy}>
          Browse…
        </button>
        <button
          style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}
          onClick={preview}
          disabled={busy}
        >
          {busy ? 'Working…' : 'Preview Changes'}
        </button>
      </div>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          color: 'var(--tm)',
          margin: '8px 0 0',
          cursor: 'pointer',
          width: 'fit-content',
        }}
      >
        <input
          type="checkbox"
          checked={autoApply}
          onChange={(e) => {
            setAuto(e.target.checked);
            void setHarAutoApply(e.target.checked);
          }}
        />
        Apply automatically at sign-in (skips this review — leave off until you trust it)
      </label>

      {busy && progress && (
        <p style={{ fontSize: 13, color: 'var(--tm)', marginTop: 16 }}>{progress}</p>
      )}
      {error && <p style={{ fontSize: 13, color: 'var(--dg)', marginTop: 16 }}>{error}</p>}

      {plan && plan.total > 0 && (
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700 }}>
              Pending changes — week of {plan.weekStart || '—'}
            </div>
            <div>
              <button style={linkBtn} onClick={() => setGroup(null, true)}>
                Select all
              </button>
              <button style={linkBtn} onClick={() => setGroup(null, false)}>
                Deselect all
              </button>
            </div>
          </div>

          <div
            style={{
              border: '1px solid var(--bd)',
              borderRadius: 6,
              maxHeight: 360,
              overflowY: 'auto',
              userSelect: 'none',
            }}
          >
            {groups.map((g) => {
              const groupRows = rows.map((r, i) => ({ r, i })).filter(({ r }) => r.group === g);
              if (groupRows.length === 0) return null;
              const groupSel = groupRows.filter(({ r }) => selected.has(r.id)).length;
              return (
                <div key={g}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '6px 12px',
                      background: 'var(--sa)',
                      borderBottom: '1px solid var(--bd)',
                      position: 'sticky',
                      top: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '.04em',
                        color: 'var(--tm)',
                      }}
                    >
                      {g} — {groupSel}/{groupRows.length} selected
                    </span>
                    <span>
                      <button style={linkBtn} onClick={() => setGroup(g, true)}>
                        All
                      </button>
                      <button style={linkBtn} onClick={() => setGroup(g, false)}>
                        None
                      </button>
                    </span>
                  </div>
                  {groupRows.map(({ r, i }) => {
                    const on = selected.has(r.id);
                    return (
                      <div
                        key={r.id}
                        onClick={(e) => onRowClick(e, i)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '5px 12px',
                          fontSize: 13,
                          cursor: 'pointer',
                          opacity: on ? 1 : 0.45,
                          background: on ? 'transparent' : 'var(--sa)',
                          borderBottom: '1px solid var(--bd)',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          readOnly
                          style={{ pointerEvents: 'none', flexShrink: 0 }}
                        />
                        <span
                          style={{
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textDecoration: on ? 'none' : 'line-through',
                          }}
                        >
                          {r.label}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--tm)', flexShrink: 0 }}>
                          {r.sub}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {(plan.skippedNoMember.length > 0 ||
            plan.skippedNoEmail.length > 0 ||
            plan.skippedTbd > 0) && (
            <p style={{ fontSize: 12, color: 'var(--tm)', margin: '8px 0 0' }}>
              Skipped automatically:
              {plan.skippedTbd > 0 ? ` ${plan.skippedTbd} TBD deliverables ·` : ''}
              {plan.skippedNoMember.length > 0
                ? ` not in SpecForma: ${plan.skippedNoMember.join(', ')} ·`
                : ''}
              {plan.skippedNoEmail.length > 0
                ? ` no email in PEOPLE_MAP: ${plan.skippedNoEmail.join(', ')}`
                : ''}
            </p>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
            <button
              style={{ ...btnPrimary, opacity: busy || selCount === 0 ? 0.6 : 1 }}
              onClick={apply}
              disabled={busy || selCount === 0}
            >
              Apply {selCount} of {rows.length} changes
            </button>
            <button style={btnSecondary} onClick={() => setPlan(null)} disabled={busy}>
              Cancel
            </button>
            <span style={{ fontSize: 12, color: 'var(--tm)' }}>
              Deselected rows are skipped; items belonging to a deselected project are skipped
              automatically.
            </span>
          </div>
        </div>
      )}

      {result && (
        <div style={{ marginTop: 20 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Projects added', value: result.projectsAdded },
              { label: 'Deliverables added', value: result.deliverablesAdded },
              { label: 'Assignments added', value: result.assignmentsAdded },
              { label: 'Forecast cells written', value: result.forecastCellsSet },
            ].map((c) => (
              <div
                key={c.label}
                style={{
                  padding: '10px 14px',
                  borderRadius: 6,
                  border: '1px solid var(--bd)',
                  background: 'var(--sa)',
                  minWidth: 120,
                }}
              >
                <div style={{ fontSize: 20, fontWeight: 700 }}>{c.value}</div>
                <div style={{ fontSize: 11, color: 'var(--tm)' }}>{c.label}</div>
              </div>
            ))}
          </div>
          {result.errors.length > 0 && (
            <p style={{ fontSize: 12, color: 'var(--dg)', margin: '8px 0 0' }}>
              <strong>Errors:</strong> {result.errors.join(' · ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
